import React, { useState } from "react";
import InputMask from "react-input-mask";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import logo from "images/lra_pink.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as SignUpIcon } from "feather-icons/dist/icons/user-plus.svg";
import { ReactComponent as LoginIcon } from "feather-icons/dist/icons/log-in.svg";
import { PrimaryButton } from "../../components/misc/Buttons.js";
import { getUserData, Register } from "helpers/Auth.js";
import { toast } from 'react-toastify';
import {
  Container, Content, MainContainer, LogoLink, LogoImage, MainContent,
  Heading, FormContainer, Form, Input, SubmitButton, ValidationText, 
  StyledModal, ModalHeading, ModalText, Spinner, LoadingWrapper
} from "./AuthStyles";

const DividerTextContainer = tw.div`my-12 border-b text-center relative`;
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const CloseModalButton = tw.button`absolute top-0 right-0 mt-8 mr-8 hocus:text-primary-500`;
const TermsText = tw.p`cursor-pointer mt-6 text-xs text-gray-600 text-center`;
const TermsUnderlined = tw.a`cursor-pointer border-b border-gray-500 border-dotted`;
const HaveAccountText = tw.p`mt-8 text-gray-600 text-center`;

export default ({
  logoLinkUrl = "/home",
  headingText = "LightRainAir",
  submitButtonText = "Register",
  SubmitButtonIcon = SignUpIcon,
  tosUrl = "https://lightrainair.co.za/static/23102023_terms_of_use-184e4afbec2fcbac36b5a8ed19b12bab.pdf",
  privacyPolicyUrl = "https://lightrainair.co.za/static/23102023_privacy_policy-92f513a554437ab715fe6ddfc33f1506.pdf",
  signInUrl = "/login",
}) => {

  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLengthValid, setLengthValid] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [emailsMatch, setEmailsMatch] = useState(true);
  const [showEmailValidation, setShowEmailValidation] = useState(false);
  const [userData, setUserdata] = useState({});
  const [group, setGroup] = useState('');
  const [region, setRegion] = useState('');
  const [result, setResult] = useState("");
  const [message, setMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [registering, setRegistering] = useState(false);

  const toggleModal = () => setModalIsOpen(!modalIsOpen);

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    setLengthValid(newPassword.length >= 8);
    setHasNumber(/\d/.test(newPassword));
    setHasUppercase(/[A-Z]/.test(newPassword));
    setHasLowercase(/[a-z]/.test(newPassword));
    setPasswordsMatch(newPassword === confirmPassword);
  };

  const handleConfirmPasswordChange = (event) => {
    const newConfirmPassword = event.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordsMatch(password === newConfirmPassword);
  };

  const handlePasswordFocus = () => {
    setShowValidation(true);
  };

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setIsValidEmail(validateEmail(newEmail));
    setEmailsMatch(newEmail === confirmEmail);
  };

  const handleConfirmEmailChange = (event) => {
    const newConfirmEmail = event.target.value;
    setConfirmEmail(newConfirmEmail);
    setEmailsMatch(email === newConfirmEmail);
  };

  const handleEmailFocus = () => {
    setShowEmailValidation(true);
  };

  const validateEmail = (email) => {
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zAZ]{2,}))$/;
    return regex.test(email);
  };

  const validateID = (scj_id) => {
    const regex = /^\d{8}-\d{5}$/;
    return regex.test(scj_id);
  }

  const populateFields = (data) => {
    setName(data.name);
    const region = document.getElementById("region");
    region.value = data.region;
    setRegion(data.region);
    const group = document.getElementById("group");
    group.value = data.group;
    setGroup(data.group);
  }

  const pullData = async (event) => {
    const scj_id = event.target.value;
    setId(scj_id);
    if (validateID(scj_id)) {
      try {
        const userdata = await getUserData(scj_id);
        const id_field = document.getElementById("reg_id");
        if (userdata === undefined) {
          id_field.value = "";
          id_field.focus = false;
          const result = {
            result: "Registering Error",
            message: `The Registration Number '${scj_id}' is Invalid.\n\nIf you believe this is an error, please inform your structure.`
          }
          setResult(result.result);
          setMessage(result.message);
          setModalIsOpen(!modalIsOpen);
        } else {
          if (userdata.Access === false){
            id_field.value = "";
            id_field.focus = false;
            const result = {
              result: "Technical Error",
              message: `The Registration Number '${scj_id}' is experiencing a Technical Error.\n\nPlease inform your structure for assistance.`
            }
            setResult(result.result);
            setMessage(result.message);
            setModalIsOpen(!modalIsOpen);
            id_field.value = "";
          } else {
            populateFields(userdata);
            toast.success('User Loaded');
          }
        }
      } catch (error) {
        toast.error('Failed to load user data. Please try again.');
      }
    }
  }

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegistering(true);
    
    const Userdata = {
      scj_id: id,
      group: group,
      region: region,
      email: email.toLowerCase(),
      lastname: lastname,
      firstname: name,
      password: password
    };
    var result = await Register(Userdata);
    setRegistering(false);
    setUserdata(Userdata);
    setResult(result[0].result);
    setMessage(result[0].message);
    if (result.length > 0) {
      setModalIsOpen(!modalIsOpen);
    }
  }

  const isFormValid = isValidEmail && emailsMatch && isLengthValid && hasNumber && hasLowercase && hasUppercase;

  return (
  <AnimationRevealPage>
    <Container>
      <Content>
        <MainContainer>
          <LogoLink href={logoLinkUrl}>
            <LogoImage src={logo} />
          </LogoLink>
          <MainContent>
            <Heading>{<>Register to <HighlightedText>{headingText}</HighlightedText></>}</Heading>
            <FormContainer>
              <Form onSubmit={(e) => handleRegister(e)}>
                <InputMask mask={"99999999-99999"} type="text" value={id} onChange={pullData} maskChar={null} maskPlaceholder={null}>
                  <Input id="reg_id" type="scj_id" pattern="^\d{8}-\d{5}$" placeholder="ID Number (00400314-12345)" value={id} onChange={pullData} required/>
                </InputMask>
                <Input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} required readOnly/>
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => handleEmailChange(e)}
                  onFocus={(e) => handleEmailFocus(e)}
                  required
                />
                { showEmailValidation && (
                  <div tw="mt-2">
                    <ValidationText valid={isValidEmail}>✓ {isValidEmail ? 'Valid Email Format' : 'Invalid Email Format'}</ValidationText>
                  </div>
                )}
                <Input
                  type="email"
                  placeholder="Confirm Email"
                  value={confirmEmail}
                  onChange={(e) => handleConfirmEmailChange(e)}
                  required
                />
                { showEmailValidation && (
                  <div tw="mt-2">
                    <ValidationText valid={emailsMatch}>
                      ✓ { emailsMatch ? "Emails Match" : "Emails Do Not Match"}
                    </ValidationText>
                  </div>
                )}
                <Input id="region" name="Region" placeholder="Region" required readOnly />
                <Input id="group" name="group" placeholder="Group" required readOnly/>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e)}
                  onFocus={(e) => handlePasswordFocus(e)}
                  required
                />
                { showValidation && (
                  <div tw="mt-2">
                    <ValidationText valid={isLengthValid}>✓ At least 8 characters</ValidationText>
                    <ValidationText valid={hasNumber}>✓ At least 1 number</ValidationText>
                    <ValidationText valid={hasUppercase}>✓ At least 1 uppercase letter</ValidationText>
                    <ValidationText valid={hasLowercase}>✓ At least 1 lowercase letter</ValidationText>
                  </div>
                )}
                <Input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => handleConfirmPasswordChange(e)}
                  required
                />
                { showValidation && (
                  <div tw="mt-2">
                    <ValidationText valid={passwordsMatch}>
                      ✓ {passwordsMatch ? "Passwords Match" : "Passwords Do Not Match"}
                    </ValidationText>
                  </div>
                )}
                <SubmitButton type="submit" disabled={!isFormValid || registering}>
                  <SubmitButtonIcon className="icon" />
                  <LoadingWrapper>
                    {registering && <Spinner />}
                    <span className="text">{registering ? "Registering..." : submitButtonText}</span>
                  </LoadingWrapper>
                </SubmitButton>
                <TermsText>
                  I agree to abide by LightRainAir's{" "}
                  <TermsUnderlined href={tosUrl}>
                    Terms of Service
                  </TermsUnderlined>{" "}
                  and its{" "}
                  <TermsUnderlined href={privacyPolicyUrl}>
                    Privacy Policy
                  </TermsUnderlined>
                </TermsText>
              </Form>
                <DividerTextContainer></DividerTextContainer>
                <HaveAccountText>
                  Already have an account?{" "}
                  <a href={signInUrl}>
                    <SubmitButton>
                      <LoginIcon className="icon" />
                      <span className="text">{"Sign In"}</span>
                    </SubmitButton>
                  </a>
                </HaveAccountText>
            </FormContainer>
              <StyledModal
                isOpen={modalIsOpen}
                onRequestClose={toggleModal}
                shouldCloseOnOverlayClick={true}
              >
                <CloseModalButton onClick={toggleModal}>
                  <CloseIcon tw="w-6 h-6" />
                </CloseModalButton>
                <MainContent>
                  <LogoLink href={logoLinkUrl}>
                    <LogoImage src={logo} />
                  </LogoLink>
                  <ModalHeading>{`${result}`}</ModalHeading>
                  <ModalText>{message}</ModalText>
                  { (result !== "Success") ?
                    <p tw="text-center">
                      <PrimaryButton onClick={toggleModal}>Back</PrimaryButton>
                    </p> : ""
                  }
                </MainContent>
              </StyledModal>
          </MainContent>
        </MainContainer>
      </Content>
    </Container>
  </AnimationRevealPage>
)};
